/**
 * Attempt state machine, respawn selection and timing rules.
 *
 * Modes:
 *  - practice: death respawns at the last checkpoint; the clock keeps running
 *              but the finish is marked as a practice time and never saved.
 *  - full-run: death fails the attempt; restart returns to the track start.
 *
 * Same-tick policy: if the player dies during a tick, no finish in that tick
 * counts, regardless of the order of contacts within the tick. This prevents
 * an undeserved finish through a lethal surface (see TESTING.md, "Lethal").
 *
 * Fair start: the clock starts when the player leaves the start zone, but only
 * once the attempt is armed. It arms while the player is in the start zone at
 * no more than run speed, and disarms if they go well past it there. So speed
 * built outside the zone (or by hopping around a large one) can't be carried
 * into a timed run; running or jumping out from a standstill always starts it.
 */
import { EventBus } from '../core/Events';
import type { DeathCause } from '../movement/PlayerState';
import type { LevelData, SpawnPoint, TriggerDef } from '../world/LevelData';
import type { TriggerEvent } from '../world/TriggerSystem';
import type { RecordStore } from './RecordStore';
import type { Split } from '../replay/RunTiming';
import { TimerSystem } from './TimerSystem';

export type GameMode = 'practice' | 'full-run';
export type AttemptStatus = 'ready' | 'running' | 'finished' | 'failed' | 'invalidated';

export interface GameplayEvents {
  'attempt-start': { attempt: number; mode: GameMode; time: number };
  'attempt-reset': { attempt: number; mode: GameMode };
  /** `split`: run time when the checkpoint was entered, sub-tick precise (like the finish). */
  checkpoint: { attempt: number; index: number; split: number };
  finish: { attempt: number; mode: GameMode; seconds: number; valid: boolean; newBest: boolean; deaths: number };
  death: { attempt: number; mode: GameMode; cause: DeathCause; colliderId: number; tick: number };
  respawn: { attempt: number; spawn: SpawnPoint; reason: 'death' | 'restart' | 'mode-change' };
  'attempt-invalidated': { attempt: number; reason: string };
  /** The finish was reached by an attempt that no longer counts (it was interrupted). Nothing is timed or stored. */
  'finish-unrecorded': { attempt: number; reason: string };
  /** The player left the start zone too fast for the attempt to arm: nothing is timed until they come back slower. */
  'start-refused': { attempt: number; speed: number; limit: number };
}

/** What the player is told when a start is refused. */
export function startRefusedText(e: GameplayEvents['start-refused']): string {
  return `Too fast to start (${e.speed.toFixed(1)} m/s): slow to run speed (${e.limit.toFixed(1)} m/s) in the start zone, then go`;
}

/** The player's relation to the start zone at the end of a tick. */
export interface StartCheck {
  /** Inside a start zone after the tick. */
  inside: boolean;
  /** Speed along the ground plane (m/s). */
  speed: number;
  /** Run speed: at or below it, being in the start zone arms the attempt. */
  runSpeed: number;
}

/** Arming allows rounding just above run speed; going this far past it in the zone disarms (a jump out of the zone keeps it). */
export const START_ARM_SLACK = 0.1;
export const START_DISARM_MARGIN = 1.5;

export interface DeathInfo {
  cause: DeathCause;
  colliderId: number;
  /** Tick-time fraction at which death occurred. */
  time: number;
}

export class AttemptManager {
  mode: GameMode = 'practice';
  status: AttemptStatus = 'ready';
  attempt = 1;
  deathsThisAttempt = 0;
  /** May the next exit from the start zone start the clock (see the fair-start note above)? */
  armed = false;
  private invalidReason = '';
  private checkpoint: TriggerDef | null = null;
  /** Checkpoints reached this attempt, in order, with their sub-tick run times. */
  readonly splits: Split[] = [];
  readonly timer: TimerSystem;

  constructor(
    private readonly level: LevelData,
    dt: number,
    private readonly records: RecordStore,
    private readonly recordKey: () => string,
    readonly events: EventBus<GameplayEvents>,
  ) {
    this.timer = new TimerSystem(dt);
  }

  get lastCheckpointIndex(): number | null {
    return this.checkpoint?.checkpointIndex ?? null;
  }

  bestTime(): number | null {
    return this.records.best(this.recordKey());
  }

  /** Where the player should appear after death or restart. */
  respawnPoint(toStart = false): SpawnPoint {
    if (!toStart && this.mode === 'practice' && this.checkpoint?.spawn) return this.checkpoint.spawn;
    return this.level.start;
  }

  /**
   * Applies one tick's trigger events and death. Returns the spawn point to
   * respawn at when the player died this tick, otherwise null.
   */
  processTick(tickIndex: number, events: readonly TriggerEvent[], death: DeathInfo | null, start: StartCheck | null = null): SpawnPoint | null {
    let entered = false;
    for (const e of events) {
      if (death && e.time > death.time) break;
      if (e.trigger.kind === 'start' && e.type === 'enter') entered = true;
      this.applyTrigger(tickIndex, e, death !== null, start, entered);
    }
    if (start && start.inside && this.status === 'ready' && !death) this.updateArming(start);
    if (!death) return null;
    return this.onDeath(tickIndex, death);
  }

  private updateArming(s: StartCheck): void {
    if (s.speed <= s.runSpeed + START_ARM_SLACK) this.armed = true;
    else if (s.speed > s.runSpeed + START_DISARM_MARGIN) this.armed = false;
  }

  /**
   * Leaving the start zone starts the clock only when armed. A zone entered and
   * left within one tick (a thin zone) counts as armed if the speed allows it.
   * Without a StartCheck (direct callers and older tests) every start is armed.
   */
  private mayStart(start: StartCheck | null, enteredThisTick: boolean): boolean {
    if (!start || this.armed) return true;
    return enteredThisTick && start.speed <= start.runSpeed + START_ARM_SLACK;
  }

  private applyTrigger(tickIndex: number, e: TriggerEvent, diedThisTick: boolean, start: StartCheck | null, enteredThisTick: boolean): void {
    const t = e.trigger;
    if (t.kind === 'start') {
      if (e.type === 'exit' && this.status === 'ready') {
        if (!this.mayStart(start, enteredThisTick)) {
          this.events.emit('start-refused', { attempt: this.attempt, speed: start!.speed, limit: start!.runSpeed });
          return;
        }
        this.status = 'running';
        this.armed = false;
        this.timer.start(tickIndex, e.time);
        this.events.emit('attempt-start', { attempt: this.attempt, mode: this.mode, time: this.timer.timeAt(tickIndex, e.time) });
      } else if (e.type === 'enter' && this.status !== 'ready') {
        this.resetAttempt();
      }
      return;
    }
    if (e.type === 'enter' && t.kind === 'finish' && this.status === 'invalidated' && !diedThisTick) {
      this.status = 'finished';
      this.events.emit('finish-unrecorded', { attempt: this.attempt, reason: this.invalidReason });
      return;
    }
    if (e.type !== 'enter' || this.status !== 'running') return;
    if (t.kind === 'checkpoint') {
      const idx = t.checkpointIndex ?? 0;
      if (this.checkpoint === null || (this.checkpoint.checkpointIndex ?? -1) !== idx) {
        this.checkpoint = t;
        const split = this.timer.at(tickIndex, e.time);
        this.splits.push({ index: idx, time: split });
        this.events.emit('checkpoint', { attempt: this.attempt, index: idx, split });
      }
      return;
    }
    if (t.kind === 'finish' && !diedThisTick) {
      const seconds = this.timer.stop(tickIndex, e.time) ?? 0;
      this.status = 'finished';
      const valid = this.mode === 'full-run' && this.deathsThisAttempt === 0;
      const newBest = valid ? this.records.submit(this.recordKey(), seconds) : false;
      this.events.emit('finish', { attempt: this.attempt, mode: this.mode, seconds, valid, newBest, deaths: this.deathsThisAttempt });
    }
  }

  private onDeath(tickIndex: number, death: DeathInfo): SpawnPoint {
    this.events.emit('death', { attempt: this.attempt, mode: this.mode, cause: death.cause, colliderId: death.colliderId, tick: tickIndex });
    this.deathsThisAttempt++;
    if (this.mode === 'full-run') {
      if (this.status === 'running') this.timer.stop(tickIndex, death.time);
      this.status = 'failed';
      const spawn = this.level.start;
      this.resetAttempt();
      return spawn;
    }
    // Practice: keep the attempt (and clock) going from the last checkpoint.
    return this.respawnPoint();
  }

  /** Manual restart. Practice returns to the last checkpoint unless `toStart`. */
  restart(toStart: boolean): SpawnPoint {
    const spawn = this.respawnPoint(toStart);
    if (this.mode === 'full-run' || toStart || this.checkpoint === null) this.resetAttempt();
    return spawn;
  }

  setMode(mode: GameMode): SpawnPoint {
    this.mode = mode;
    this.resetAttempt();
    return this.level.start;
  }

  /** Full-run attempts become invalid on pause or simulation stall. */
  invalidate(reason: string): void {
    if (this.mode !== 'full-run' || this.status !== 'running') return;
    this.status = 'invalidated';
    this.invalidReason = reason;
    this.timer.reset();
    this.events.emit('attempt-invalidated', { attempt: this.attempt, reason });
  }

  private resetAttempt(): void {
    this.attempt++;
    this.status = 'ready';
    this.armed = false;
    this.deathsThisAttempt = 0;
    this.checkpoint = null;
    this.splits.length = 0;
    this.timer.reset();
    this.events.emit('attempt-reset', { attempt: this.attempt, mode: this.mode });
  }
}
